import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [pauseButton] - The pause/play button (optional).
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement, pauseButton } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Set up pause/play button if it exists
    if (pauseButton) {
      const { signal } = this.#controller;
      pauseButton.addEventListener('click', this.#togglePlayPause, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Toggles play/pause state of the background video
   * @private
   */
  #togglePlayPause = () => {
    const { videoElement, pauseButton } = this.refs;

    if (!videoElement || !pauseButton) return;

    const pauseIcon = pauseButton.querySelector('.video-background__control-icon--pause');
    const playIcon = pauseButton.querySelector('.video-background__control-icon--play');

    if (videoElement.paused) {
      videoElement.play();
      pauseIcon?.classList.remove('hidden');
      playIcon?.classList.add('hidden');
      pauseButton.setAttribute('aria-label', pauseButton.getAttribute('aria-label')?.replace('Play', 'Pause') || 'Pause video');
    } else {
      videoElement.pause();
      pauseIcon?.classList.add('hidden');
      playIcon?.classList.remove('hidden');
      pauseButton.setAttribute('aria-label', pauseButton.getAttribute('aria-label')?.replace('Pause', 'Play') || 'Play video');
    }
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
